import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { GOOGLE_CLIENT_ID } from ".";
import Layout from "./components/layout/Layout";
import Login from "./components/Login";
import { Spinner } from "./components/ui/spinner";
import { PLACEHOLDER_DOCUMENT_ID } from "./consts";
import { AppProvider } from "./contexts/AppContext";
import { useUser } from "./hooks/useUser";
import { Document } from "./pages/Document";
import Documents from "./pages/Documents";
import KnowledgeBase from "./pages/KnowledgeBase";
import { Looker } from "./pages/Looker";
import ScheduleMeeting from "./pages/ScheduleMeeting";
import {
  BASE_URL,
  getGoogleRefreshToken,
  getUserEmail,
  setAuthToken,
  setGoogleAccessToken,
  setGoogleRefreshToken,
  setUserEmail,
} from "./services/auth";

function AppContent() {
  const [isAuthenticated, setAuthentication] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  const url = useMemo(() => {
    return new URL(window.location.href);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  // console.log("Current authentication state:", isAuthenticated);
  async function login(hint?: string) {
    setLoading(true);
    const clientId = GOOGLE_CLIENT_ID;
    const redirectUri = false
      ? "http://localhost:3000"
      : "https://dash.flashdocs.ai";
    const scope = "email profile";
    const responseType = "code";

    let prompt = "consent";
    const refreshToken = getGoogleRefreshToken();
    if (refreshToken) {
      prompt = "none";
    }

    const hintUrl = hint ? `&login_hint=${hint}` : "";

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${encodeURIComponent(
      scope
    )}${prompt === "consent" ? "&access_type=offline" : ""}${hintUrl}${
      prompt === "consent" ? "&prompt=consent" : ""
    }`;

    window.location.href = authUrl;

    setLoading(false);
  }

  // console.log({ loading });

  useEffect(() => {
    async function A() {
      const code = new URLSearchParams(url.search).get("code");
      if (!code) return;
      const res = await axios.post(
        `${BASE_URL}/auth/token/google-access?code=` + code
      );
      if ("refresh_token" in res.data && "access_token" in res.data) {
        setGoogleRefreshToken(res.data.refresh_token);
        setGoogleAccessToken(res.data.access_token);

        // get auth token
        const userInfo = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${res.data.access_token}` } }
        );

        const email = userInfo.data.email;
        setUserEmail(email);

        if (!email) throw new Error("No email found");
        // console.log("calling debug-token...", error.config);
        const params = {
          email,
          access_token: res.data.access_token,
          google_document_id: PLACEHOLDER_DOCUMENT_ID,
        };
        const tokenCall = await axios.get(
          BASE_URL +
            "/auth/token/access-token?" +
            new URLSearchParams(params).toString()
        );
        const { access_token } = tokenCall.data;

        setAuthToken(access_token);
        setAuthentication(true);

        window.location.href = "/";
      } else if ("access_token" in res.data) {
        setGoogleAccessToken(res.data.access_token);

        // get auth token
        const userInfo = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${res.data.access_token}` } }
        );

        const email = userInfo.data.email;
        setUserEmail(email);

        if (!email) throw new Error("No email found");
        // console.log("calling debug-token...", error.config);
        const params = {
          email,
          access_token: res.data.access_token,
          google_document_id: PLACEHOLDER_DOCUMENT_ID,
        };
        const tokenCall = await axios.get(
          BASE_URL +
            "/auth/token/access-token?" +
            new URLSearchParams(params).toString()
        );
        const { access_token } = tokenCall.data;

        setAuthToken(access_token);
        setAuthentication(true);

        window.location.href = "/";
      }
    }

    setLoading(true);
    A().finally(() => setLoading(false));
  }, [url]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user && user.email) {
      setAuthentication(true);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      if (
        user &&
        user.organization_id === "DEFAULT" &&
        !user.email.endsWith("@flashdocs.ai") &&
        window.location.pathname !== "/schedule-meeting"
      ) {
        window.location.href = `${window.location.origin}/schedule-meeting`;
      }
    }
    // eslint-disable-next-line
  }, [isAuthenticated, JSON.stringify(user)]);

  if (loading) {
    return (
      <div className="w-full h-full flex flex-col gap-6 items-center justify-center">
        <div className="h-stack gap-3">
          <Spinner />
          <p className="text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        setAuth={setAuthentication}
        onLoginSuccess={() => login(getUserEmail() || undefined)}
      />
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <Layout
              onLogout={() => {
                setAuthentication(false);
                sessionStorage.clear();
              }}
            />
          }
        >
          <Route path="/" element={<Documents />} />
          <Route path="/document/:id" element={<Document />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          <Route path="/knowledge-base/:itemId" element={<KnowledgeBase />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
        <Route path="/schedule-meeting" element={<ScheduleMeeting />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
